import { Component } from '@theme/component';

/**
 * TRB seamless announcement marquee.
 *
 * Renders a single readable message track plus an `aria-hidden` duplicate, then
 * scrolls both left -> right with a single CSS `@keyframes` animation on
 * `transform` (GPU-friendly, no layout thrash).
 *
 * Speed is authored in px/s so the bar reads at an even pace regardless of how
 * much copy is in it. We measure the readable track width and derive the
 * `animation-duration` as `width / speed`.
 *
 * Initialised on visibility via `IntersectionObserver`: the animation is paused
 * while the bar is offscreen and resumed when it scrolls back into view. The
 * whole component is a progressive enhancement — without JS the CSS still shows
 * a static, readable bar.
 *
 * Reduced motion is handled in CSS (`@media (prefers-reduced-motion: reduce)`);
 * we additionally bail out of starting the animation here so we never burn a
 * frame on devices that asked for stillness.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The readable (measured) message track.
 * @property {HTMLElement[]} tracks - Both tracks (readable + aria-hidden clone).
 *
 * @extends Component<Refs>
 */
class TrbMarquee extends Component {
  requiredRefs = ['track'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {boolean} */
  #visible = false;

  /** @type {MediaQueryList | null} */
  #reducedMotion = null;

  connectedCallback() {
    super.connectedCallback();

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Honour reduced motion up front: leave the bar static, skip all observers.
    if (this.#reducedMotion.matches) return;

    this.#reducedMotion.addEventListener('change', this.#handleMotionPreference);

    // Recompute duration whenever the track's width changes (font load, resize,
    // editor edits) so px/s stays accurate.
    this.#resizeObserver = new ResizeObserver(() => this.#updateDuration());
    this.#resizeObserver.observe(this.refs.track);

    // Init + pause/resume on visibility.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#visible = entry.isIntersecting;
          this.#applyPlayState();
        }
      },
      { threshold: 0 }
    );
    this.#observer.observe(this);

    this.#updateDuration();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#reducedMotion?.removeEventListener('change', this.#handleMotionPreference);
  }

  /**
   * Reduced-motion preference changed at runtime (e.g. OS toggle).
   * @type {(event: MediaQueryListEvent) => void}
   */
  #handleMotionPreference = (event) => {
    if (event.matches) {
      this.style.removeProperty('--trb-marquee-duration');
    } else {
      this.#updateDuration();
      this.#applyPlayState();
    }
  };

  /**
   * Derive `animation-duration` from track width (px) ÷ speed (px/s).
   * The keyframe translates by exactly one track width, so one cycle covers a
   * single readable copy at the requested pixels-per-second.
   */
  #updateDuration() {
    if (this.#reducedMotion?.matches) return;

    const speed = Number(this.dataset.speed);
    const width = this.refs.track?.getBoundingClientRect().width ?? 0;

    if (!speed || width <= 0) return;

    const duration = width / speed;
    this.style.setProperty('--trb-marquee-duration', `${duration}s`);
  }

  /** Pause the CSS animation while offscreen; resume when visible. */
  #applyPlayState() {
    if (this.#reducedMotion?.matches) return;
    this.dataset.play = this.#visible ? 'true' : 'false';
  }
}

if (!customElements.get('trb-marquee')) {
  customElements.define('trb-marquee', TrbMarquee);
}
